import React, { useState, useContext, useEffect } from "react";
import companyBasedRequest from "../../../request/companyBasedRequest";
import translations from "../../../translations/translations";
import "../adminModals.css";
import "./shopInfoModal.css";
import { CombinedContext } from "../../../context/combinedContext";
import RSFNewAutocomplete from "../../../utils/addressAutofill/addressAutocomplete";
import BannerMessage from "../../common/bannerMessage/bannerMessage";
import { Info } from "lucide-react";
import useModalDismiss from "../../common/useModalDismiss";

const DESCRIPTION_MAX = 500;

const ShopInfoModal = ({ open, onClose, shopData, setShopData }) => {
    useModalDismiss(onClose, { enabled: open });
    const { language, userData, setUserData } = useContext(CombinedContext);
    const [bannerMessage, setBannerMessage] = useState("");
    const [invalidFields, setInvalidFields] = useState(new Set());
    const [saving, setSaving] = useState(false);

    const [localData, setLocalData] = useState({
        name: "",
        description: "",
        address: "",
        city: "",
        country: "",
        addressDescription: "",
        lat: null,
        lng: null,
    });

    useEffect(() => {
        if (shopData) {
            setLocalData({
                name: shopData.name || "",
                description: shopData.description || "",
                address: shopData.address || "",
                city: shopData.city || "",
                country: shopData.country || "",
                addressDescription: shopData.addressDescription || "",
                lat: shopData.lat ?? null,
                lng: shopData.lng ?? null,
            });
        }
    }, [shopData]);

    if (!open) return null;

    const t = translations[language].profile;
    const infoT = t.modal.shopInfo;

    const clearInvalid = (field) => {
        setInvalidFields((prev) => {
            const next = new Set(prev);
            next.delete(field);
            return next;
        });
    };

    const invalidClass = (field) => (invalidFields.has(field) ? " is-invalid" : "");

    const handleSave = async () => {
        const invalid = new Set();
        if (!localData.name.trim()) invalid.add("name");
        if (!localData.description.trim()) invalid.add("description");
        if (!localData.address.trim()) invalid.add("address");
        if (!localData.city.trim()) invalid.add("city");
        if (!localData.country.trim()) invalid.add("country");

        if (invalid.size > 0) {
            setInvalidFields(invalid);
            setBannerMessage(translations[language].profile.errors.fillAllFields || "Please fill in all fields.");
            return;
        }

        setInvalidFields(new Set());
        setBannerMessage("");

        try {
            setSaving(true);
            const res = await companyBasedRequest.updateShopInfo(
                shopData.shopId,
                { ...localData },
                userData.cognitoTokens.AccessToken
            );
            setUserData((prev) => ({ ...prev, shop: res.shop }));
            onClose();
        } catch (err) {
            console.error("Failed to update shop info:", err);
        } finally {
            setSaving(false);
        }
    };

    const descriptionCount = localData.description.length;

    return (
        <div className="sf-pm-overlay">
            <div className="sf-pm-modal">
                {/* HEADER */}
                <div className="sf-pm-header">
                    <div className="sf-pm-heading">
                        <h2 className="sf-pm-title">{infoT.title}</h2>
                        <p className="sf-pm-sub">{infoT.intro}</p>
                    </div>
                    <button className="sf-pm-close" onClick={onClose} aria-label={t.actions.cancel}>
                        ✕
                    </button>
                </div>

                {/* BODY */}
                <div className="sf-pm-body">
                    {/* DETAILS */}
                    <section className="sf-pm-section">
                        <p className="sf-pm-label">{infoT.subTitle}</p>

                        <div className="sf-pm-card">
                            <div className="sf-pm-field">
                                <label className="sf-pm-field-label" htmlFor="sf-pm-shop-name">
                                    {infoT.name}
                                </label>
                                <input
                                    id="sf-pm-shop-name"
                                    className={`sf-pm-input${invalidClass("name")}`}
                                    placeholder={infoT.name}
                                    value={localData.name}
                                    onChange={(e) => {
                                        setLocalData({ ...localData, name: e.target.value });
                                        clearInvalid("name");
                                    }}
                                />
                            </div>

                            <div className="sf-pm-field">
                                <label className="sf-pm-field-label" htmlFor="sf-pm-shop-desc">
                                    {infoT.description}
                                </label>
                                <div className="sf-pm-counted">
                                    <textarea
                                        id="sf-pm-shop-desc"
                                        className={`sf-pm-textarea${invalidClass("description")}`}
                                        placeholder={infoT.description}
                                        value={localData.description}
                                        maxLength={DESCRIPTION_MAX}
                                        onChange={(e) => {
                                            setLocalData({ ...localData, description: e.target.value });
                                            clearInvalid("description");
                                        }}
                                    />
                                    <span
                                        className={`sf-pm-counter${
                                            descriptionCount >= DESCRIPTION_MAX - 40 ? " is-warn" : ""
                                        }`}
                                    >
                                        {descriptionCount}/{DESCRIPTION_MAX}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* LOCATION */}
                    <section className="sf-pm-section">
                        <p className="sf-pm-label">{t.sidebar.location.title}</p>

                        <div className="sf-pm-card">
                            {/* Replaces the hover tooltip this modal used to carry — there is
                                no hover on a phone, so the guidance now sits in the flow. */}
                            <p className="sf-pm-hint">
                                <Info size={16} strokeWidth={1.9} />
                                <span>{infoT.locationHint}</span>
                            </p>

                            <div className="sf-pm-field">
                                <label className="sf-pm-field-label">{infoT.address}</label>
                                <div className={`sf-pm-autocomplete${invalidClass("address")}`}>
                                    <RSFNewAutocomplete
                                        value={localData.address}
                                        onChange={(val) => {
                                            setLocalData((prev) => ({ ...prev, address: val, lat: null, lng: null }));
                                            clearInvalid("address");
                                        }}
                                        onSelect={(data) => {
                                            setLocalData((prev) => ({
                                                ...prev,
                                                address: data.address,
                                                city: data.city,
                                                country: data.country,
                                                lat: data.lat,
                                                lng: data.lng,
                                            }));
                                            clearInvalid("address");
                                        }}
                                        language={language}
                                    />
                                </div>
                            </div>

                            <div className="sf-pm-field">
                                <label className="sf-pm-field-label" htmlFor="sf-pm-shop-addr2">
                                    {language === "es" ? "Piso, suite, referencia" : "Floor, suite, landmark"}
                                </label>
                                <input
                                    id="sf-pm-shop-addr2"
                                    className="sf-pm-input"
                                    placeholder={language === "es" ? "Opcional" : "Optional"}
                                    value={localData.addressDescription}
                                    onChange={(e) => setLocalData({ ...localData, addressDescription: e.target.value })}
                                />
                            </div>

                            <div className="sf-pm-grid-2">
                                <div className="sf-pm-field">
                                    <label className="sf-pm-field-label" htmlFor="sf-pm-shop-city">
                                        {infoT.city}
                                    </label>
                                    <input
                                        id="sf-pm-shop-city"
                                        className={`sf-pm-input${invalidClass("city")}`}
                                        placeholder={infoT.city}
                                        value={localData.city}
                                        onChange={(e) => {
                                            setLocalData({ ...localData, city: e.target.value });
                                            clearInvalid("city");
                                        }}
                                    />
                                </div>

                                <div className="sf-pm-field">
                                    <label className="sf-pm-field-label" htmlFor="sf-pm-shop-country">
                                        {infoT.country}
                                    </label>
                                    <input
                                        id="sf-pm-shop-country"
                                        className={`sf-pm-input${invalidClass("country")}`}
                                        placeholder={infoT.country}
                                        value={localData.country}
                                        onChange={(e) => {
                                            setLocalData({ ...localData, country: e.target.value });
                                            clearInvalid("country");
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                {/* FOOTER */}
                <div className="sf-pm-footer">
                    <button className="sf-pm-btn is-ghost" onClick={onClose} disabled={saving}>
                        {translations[language].calendar.editor.cancel}
                    </button>
                    <button className="sf-pm-btn is-primary" onClick={handleSave} disabled={saving}>
                        {saving && <span className="sf-pm-spinner" aria-hidden="true" />}
                        {saving
                            ? translations[language].calendar.editor.saving
                            : translations[language].calendar.editor.save}
                    </button>
                </div>
            </div>
            <BannerMessage type="error" text={bannerMessage} />
        </div>
    );
};

export default ShopInfoModal;
